import { ReactNode, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Home,
  Calendar,
  Rss,
  Sparkles,
  MessageSquare,
  Users,
  UserPlus,
  Trophy,
  Download,
  Settings,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { UserButton, useUser } from "@clerk/clerk-react";
import { isAdmin } from "@/lib/admin";

interface LayoutProps {
  children: ReactNode;
}

const LayoutInner = ({ children }: LayoutProps) => {
  const { user } = useUser();
  const { setOpenMobile } = useSidebar();
  const { pathname } = useLocation();

  // Close mobile sidebar on navigation
  useEffect(() => {
    setOpenMobile(false);
  }, [pathname, setOpenMobile]);

  const userEmail = user?.primaryEmailAddress?.emailAddress;
  const showAdmin = isAdmin(userEmail);

  const mainMenuItems = [
    { icon: Home, title: "Dashboard", url: "/" },
    { icon: Calendar, title: "Events", url: "/events" },
    { icon: Rss, title: "Updates", url: "/feed" },
    { icon: Trophy, title: "Conference", url: "/conference" },
    { icon: Users, title: "Members", url: "/members" },
    { icon: UserPlus, title: "Refer a Peer", url: "/refer" },
    ...(showAdmin ? [{ icon: Settings, title: "Admin", url: "/admin" }] : []),
  ];

  const resourcesMenuItems = [
    { icon: Sparkles, title: "AlexAI Pro", url: "/ai" },
    { icon: MessageSquare, title: "WhatsApp", url: "/whatsapp" },
    { icon: Download, title: "How to Install the App", url: "/install-app" },
  ];

  const handleNavClick = () => setOpenMobile(false);

  const SidebarNavLink = ({ to, icon: Icon, label }: { to: string; icon: React.ElementType; label: string }) => {
    const resolved = useLocation();
    const isActive = resolved.pathname === to || (to !== "/" && resolved.pathname.startsWith(to));

    return (
      <SidebarMenuItem>
        <SidebarMenuButton asChild isActive={isActive}>
          <NavLink
            to={to}
            onClick={handleNavClick}
            className="text-sidebar-foreground/70 hover:text-sidebar-foreground"
            style={isActive ? { boxShadow: "inset 3px 0 0 hsl(220 15% 65%)" } : undefined}
          >
            <Icon className="w-4 h-4 opacity-70" />
            <span className="tracking-wide text-sm">{label}</span>
          </NavLink>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  return (
    <div className="min-h-screen flex w-full flex-col">
      {/* Mobile header */}
      <header className="md:hidden sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur-sm supports-[backdrop-filter]:bg-background/80">
        <div className="flex h-14 items-center px-4 gap-4">
          <SidebarTrigger className="text-foreground/80 hover:text-foreground" />
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold tracking-refined">ETP Network</h2>
          </div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar with gradient background */}
        <Sidebar className="bg-gradient-to-b from-sidebar to-[hsl(225,28%,10%)]">
          <SidebarContent>
            {/* Brand header */}
            <NavLink to="/" className="block p-6 pb-5 border-b border-sidebar-border/50 hover:bg-sidebar-accent/20 transition-colors duration-200">
              <div className="flex flex-col gap-0.5">
                <h2 className="text-lg font-semibold text-sidebar-foreground tracking-refined leading-tight">
                  Energy Talking Points
                </h2>
                <p className="text-xs uppercase tracking-elegant text-sidebar-foreground/50 font-medium">
                  Executive Network
                </p>
              </div>
            </NavLink>

            <SidebarGroup className="pt-4">
              <SidebarGroupLabel className="text-[10px] uppercase tracking-elegant text-sidebar-foreground/40 font-medium px-3">
                Navigation
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {mainMenuItems.map((item) => (
                    <SidebarNavLink key={item.title} to={item.url} icon={item.icon} label={item.title} />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            <SidebarGroup>
              <SidebarGroupLabel className="text-[10px] uppercase tracking-elegant text-sidebar-foreground/40 font-medium px-3">
                Resources & Tools
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {resourcesMenuItems.map((item) => (
                    <SidebarNavLink key={item.title} to={item.url} icon={item.icon} label={item.title} />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            </SidebarContent>
            
            {/* User footer */}
            {user && (
              <SidebarFooter>
                <div className="flex items-center gap-3 p-4 border-t border-sidebar-border/30 bg-sidebar-accent/20">
                  <UserButton
                    afterSignOutUrl="/"
                    appearance={{
                      elements: {
                        avatarBox: "h-9 w-9 ring-1 ring-sidebar-foreground/10",
                        userButtonTrigger: "focus:shadow-none"
                      }
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-sidebar-foreground truncate tracking-wide">
                      {user.fullName || user.firstName || "User"}
                    </p>
                    <p className="text-xs text-sidebar-foreground/50 truncate">
                      {user.primaryEmailAddress?.emailAddress}
                    </p>
                  </div>
                </div>
              </SidebarFooter>
            )}
          </Sidebar>

        {/* Main content area */}
        <main className="flex-1 overflow-auto bg-gradient-to-br from-background via-background to-muted/30">
          {children}
        </main>
      </div>
    </div>
  );
};

const Layout = ({ children }: LayoutProps) => (
  <SidebarProvider>
    <LayoutInner>{children}</LayoutInner>
  </SidebarProvider>
);

export default Layout;
